import React, { useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useParams } from "react-router";
import {
  COMPONENT_LIST_INIT_SORT,
  FILE_BROWSER_ROUTES,
  REPORT_PAGES_ROUTES,
} from "@sscs/report/constants";
import { BadgeWrapperTarget, ComponentList } from "@sscs/report/ui";
import { convertFinalCheckLabel } from "@sscs/report/utils";
import {
  ComponentListSort,
  ComponentSummary,
  Pagination as PaginationType,
} from "@sscs/types";
import { Table } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { LinkToReport } from "Components";
import useSearch from "../../../../hooks/useSearch";
import { useGetIssueVersionComponentsQuery } from "../../../../services/insights.api";
import { InsightType, InsightsVersion } from "../../../../types";
import styles from "./components.module.scss";

type ComponentPriorityAndIssuesProps = {
  rowIndex: number;
  version: InsightsVersion;
  component: ComponentSummary;
  isRepro: boolean;
  target: BadgeWrapperTarget;
  children: React.ReactNode;
};

const PriorityAndIssuesLink = ({
  version,
  component,
  isRepro,
  rowIndex,
  target,
  children,
}: ComponentPriorityAndIssuesProps): React.JSX.Element => {
  if (target === "classification") {
    return (
      <LinkToReport
        id={`info-link-${rowIndex}`}
        type="text"
        report={{
          projectName: version.project_name,
          packageName: version.package_name,
          version: version.software_info.version,
          isRepro: isRepro,
          componentPath: component.path,
        }}
        goToPage={`${REPORT_PAGES_ROUTES.FILE_BROWSER_ROUTE}/${component.id}/${FILE_BROWSER_ROUTES.INFO_ROUTE}`}
      >
        {children}
      </LinkToReport>
    );
  } else if (target === "issues") {
    return (
      <LinkToReport
        id={`issues-link-${rowIndex}`}
        type="text"
        report={{
          projectName: version.project_name,
          packageName: version.package_name,
          version: version.software_info.version,
          isRepro: isRepro,
          componentPath: component.path,
        }}
        goToPage={`${REPORT_PAGES_ROUTES.FILE_BROWSER_ROUTE}/${component.id}/${FILE_BROWSER_ROUTES.ISSUES_ROUTE}`}
      >
        {children}
      </LinkToReport>
    );
  } else {
    return (
      <LinkToReport
        id={`vulnerabilities-link-${rowIndex}`}
        type="text"
        report={{
          projectName: version.project_name,
          packageName: version.package_name,
          version: version.software_info.version,
          isRepro: isRepro,
          componentPath: component.path,
        }}
        goToPage={`${REPORT_PAGES_ROUTES.FILE_BROWSER_ROUTE}/${component.id}/${FILE_BROWSER_ROUTES.VULNERABILITIES_ROUTE}`}
      >
        {children}
      </LinkToReport>
    );
  }
};

type ComponentsProps = {
  issueId: string;
  issueType: InsightType;
  version: InsightsVersion;
  rowIndex: number;
};

const Components = ({
  issueId,
  issueType,
  version,
  rowIndex,
}: ComponentsProps): React.JSX.Element => {
  const { groupId } = useParams();
  const { filters, isSearchFetching } = useSearch();
  const [pagination, setPagination] = useState<PaginationType>({
    currentPage: 1,
    itemsPerPage: 10,
  });
  const [sort, setSort] = useState<ComponentListSort>(COMPONENT_LIST_INIT_SORT);
  const { data, isFetching, isLoading, isError, error } =
    useGetIssueVersionComponentsQuery(
      groupId && !isSearchFetching
        ? {
            id: issueId,
            type: issueType,
            reportId: version.scan_report_id,
            filters,
            groupId,
            pagination,
            sort,
          }
        : skipToken,
    );
  const { isRepro } = convertFinalCheckLabel(version.scan_report.checks);

  return (
    <ComponentList.Table
      itemId="insights"
      rowIndex={rowIndex}
      title="List of components affected by this issue"
      matchCount={data?.match_count ?? 0}
      queryStatus={getQueryStatus({
        isLoading,
        isFetching,
        isError,
        error,
        data,
      })}
      totalCount={data?.match_count ?? 0}
      pagination={pagination}
      onPaginationChange={setPagination}
      columns={ComponentList.columns({
        sortBy: sort,
        rowIndex,
        onSortChange: setSort,
      })}
    >
      {data?.components.map((component, index) => (
        <Table.Row key={component.id ?? component.path}>
          <Table.Cell>
            <ComponentList.PriorityAndIssues
              component={component}
              id={index.toString()}
              badgeWrapper={(target, children) => (
                <PriorityAndIssuesLink
                  rowIndex={rowIndex}
                  version={version}
                  component={component}
                  isRepro={isRepro}
                  target={target}
                >
                  {children}
                </PriorityAndIssuesLink>
              )}
            />
          </Table.Cell>
          <Table.Cell>
            <ComponentList.Name
              component={component}
              nameWrapper={(children) => (
                <LinkToReport
                  id={`component-${rowIndex}`}
                  report={{
                    projectName: version.project_name,
                    packageName: version.package_name,
                    version: version.software_info.version,
                    isRepro: isRepro,
                    componentPath: component.path,
                  }}
                  className={styles.link}
                  goToPage={`${REPORT_PAGES_ROUTES.FILE_BROWSER_ROUTE}/${component.id}/${FILE_BROWSER_ROUTES.INFO_ROUTE}`}
                >
                  {children}
                </LinkToReport>
              )}
            />
          </Table.Cell>
          <Table.Cell>
            <ComponentList.Path path={component.path} />
          </Table.Cell>
        </Table.Row>
      ))}
    </ComponentList.Table>
  );
};

export default Components;
